/**
 * Turns a track (types.ts) into timed events: every pluck, held phrase note and drum hit with its
 * time in seconds. Techniques that are many strokes (摇指 tremolo, 刮奏 glissando, grace notes) are
 * spelled out here. The only variation (a little looseness of time and touch, an added 揉 on long
 * notes) comes from the track's seed and the loop count, so a track always plays the same way.
 */
import { mulberry32, type Rng } from '@nine/shared';
import { lineBeats, parseLine, type Pitch, type Tech, type Tok } from './notation';
import { degreeMidi, pentatonicStep, type Key } from './theory';
import { INSTRUMENTS, type Instrument, type SectionDef, type TrackDef } from './types';

export interface MusicEvent {
  /** Seconds from the start of its block (the intro, or one pass of the loop). */
  readonly t: number;
  /** Written length in seconds. */
  readonly dur: number;
  readonly part: string;
  readonly inst: Instrument;
  /** Pitch (MIDI); 0 for drums. */
  readonly midi: number;
  /** The drum, for percussion. */
  readonly drum?: string;
  /** Loudness, about 0–1.4. */
  readonly vel: number;
  readonly techs: readonly Tech[];
  /** Plucked notes: seconds the string rings before it is damped. */
  readonly ring?: number;
  /** Held voices: notes of one phrase share one tone that glides between them. */
  readonly phrase?: number;
}

export interface CompiledBlock {
  readonly events: readonly MusicEvent[];
  /** Length in seconds. */
  readonly dur: number;
  /** Where each section starts (seconds), in order. */
  readonly sections: readonly { readonly name: string; readonly t: number }[];
}

export interface CompiledTrack {
  readonly def: TrackDef;
  readonly intro: CompiledBlock | null;
  readonly body: CompiledBlock;
}

/** Seconds from a section's start to beat `b`, the tempo easing from bpm to bpm × rit across it. */
export function sectionTime(bpm: number, rit: number, beats: number, b: number): number {
  if (rit === 1 || beats <= 0) return (b * 60) / bpm;
  const k = (bpm * rit - bpm) / beats;
  return (60 / k) * Math.log((bpm + k * b) / bpm);
}

/** Length of a section in beats, checking that every part fills it exactly. */
export function sectionBeats(def: TrackDef, name: string): number {
  const s = def.sections[name];
  if (!s) throw new Error(`${def.id}: no section "${name}"`);
  const want = s.bars * def.meter;
  for (const [part, line] of Object.entries(s.parts)) {
    if (!def.parts[part]) throw new Error(`${def.id}/${name}: part "${part}" is not in the band`);
    const got = lineBeats(parseLine(line, def.parts[part].inst === 'perc'));
    if (Math.abs(got - want) > 1e-6) throw new Error(`${def.id}/${name}/${part}: ${got} beats, want ${want}`);
  }
  return want;
}

const TREMOLO_RATE: Partial<Record<Instrument, number>> = { zheng: 12, pipa: 15, qin: 9 };
/** Extra seconds a plucked string rings past its written length before it is damped. */
const RING: Partial<Record<Instrument, number>> = { zheng: 1.6, qin: 2.6, pipa: 0.35 };

interface Ctx {
  readonly def: TrackDef;
  readonly rng: Rng;
  readonly out: MusicEvent[];
  phrase: number;
}

const midiOf = (key: Key, p: Pitch) => degreeMidi(key, p.degree, p.octave, p.acc);

function compileSection(c: Ctx, name: string, at: number): number {
  const { def, rng, out } = c;
  const s: SectionDef = def.sections[name]!;
  const beats = sectionBeats(def, name);
  const bpm = s.bpm ?? def.bpm;
  const rit = s.rit ?? 1;
  const time = (b: number) => at + sectionTime(bpm, rit, beats, b);
  const loose = def.humanize ?? 0.4;
  for (const [part, line] of Object.entries(s.parts)) {
    const pd = def.parts[part]!;
    const inst = pd.inst;
    const info = INSTRUMENTS[inst];
    const toks: Tok[] = parseLine(line, inst === 'perc');
    let b = 0;
    let inPhrase = false;
    for (const tok of toks) {
      if (tok.kind === 'breath') {
        inPhrase = false;
        continue;
      }
      const start = b;
      const t0 = time(b);
      const dur = time(b + tok.beats) - t0;
      b += tok.beats;
      if (tok.kind === 'rest') {
        inPhrase = false;
        continue;
      }
      // A little looseness: never on the downbeat of a bar, so the loop and the sections line up.
      const downbeat = Math.abs(start / def.meter - Math.round(start / def.meter)) < 1e-6;
      const jitter = downbeat ? 0 : (rng() - 0.5) * 0.02 * loose;
      const touch = 1 + (rng() - 0.5) * 0.18 * loose;
      const t = Math.max(0, t0 + jitter);
      if (tok.kind === 'hit') {
        for (const drum of tok.drums)
          out.push({ t, dur, part, inst, midi: 0, drum, vel: tok.dyn * tok.accent * touch, techs: [] });
        continue;
      }
      const vel = tok.dyn * tok.accent * touch;
      const techs = [...tok.techs];
      // Long plucked notes are often given a 揉 by the left hand.
      if (info.kind === 'pluck' && !techs.length && tok.beats >= 1.5 && rng() < 0.3) techs.push('rou');
      if (info.kind === 'held' || info.kind === 'pad') {
        if (info.kind === 'held' && !inPhrase) c.phrase++;
        inPhrase = info.kind === 'held';
        for (const p of tok.pitches)
          out.push({ t, dur, part, inst, midi: midiOf(def.key, p), vel, techs, phrase: c.phrase });
        if (tok.grace && info.kind === 'held') {
          // A held grace note: the phrase touches it for an instant before the note.
          const g = midiOf(def.key, tok.grace);
          out.push({ t: Math.max(0, t - 0.07), dur: 0.07, part, inst, midi: g, vel, techs: [], phrase: c.phrase });
        }
        continue;
      }
      // Plucked strings.
      for (const p of tok.pitches) {
        let midi = midiOf(def.key, p);
        const t2 = [...techs];
        // On the zheng, 4 and 7 are not open strings: the string below is pressed up to them (按音).
        if (inst === 'zheng' && p.acc === 0 && (p.degree === 4 || p.degree === 7) && !t2.includes('an'))
          t2.push('an');
        const ring = dur + (RING[inst] ?? 1);
        if (tok.grace) {
          const g = midiOf(def.key, tok.grace);
          out.push({ t: Math.max(0, t - 0.08), dur: 0.08, part, inst, midi: g, vel: vel * 0.7, techs: [], ring: 0.3 });
        }
        if (t2.includes('glissUp') || t2.includes('glissDown')) gliss(c, part, inst, midi, t, vel, t2.includes('glissUp'));
        if (t2.includes('trem') && TREMOLO_RATE[inst]) {
          tremolo(c, part, inst, midi, t, dur, vel, t2);
          continue;
        }
        midi = Math.max(info.range[0], Math.min(info.range[1], midi));
        out.push({ t, dur, part, inst, midi, vel, techs: t2, ring });
      }
    }
  }
  return at + sectionTime(bpm, rit, beats, beats);
}

/** 刮奏: the fingers sweep across the open strings into the note (from below, or from above). */
function gliss(c: Ctx, part: string, inst: Instrument, midi: number, t: number, vel: number, up: boolean): void {
  const n = 9;
  const span = Math.min(0.42, t);
  if (span <= 0.05) return;
  const [lo, hi] = INSTRUMENTS[inst].range;
  // Start on the nearest open string, so the sweep stays on the scale.
  const start = pentatonicStep(c.def.key, pentatonicStep(c.def.key, midi, 1), -1);
  for (let i = 0; i < n; i++) {
    const m = pentatonicStep(c.def.key, start, up ? -(n - i) : n - i);
    if (m < lo || m > hi) continue;
    const at = t - span + (span * i) / n;
    const v = vel * (0.3 + (0.4 * i) / n);
    c.out.push({ t: at, dur: span / n, part, inst, midi: m, vel: v, techs: [], ring: 0.9 });
  }
}

/** 摇指: the same string plucked back and forth fast for the note's length, swelling a little. */
function tremolo(
  c: Ctx,
  part: string,
  inst: Instrument,
  midi: number,
  t: number,
  dur: number,
  vel: number,
  techs: Tech[],
): void {
  const rate = TREMOLO_RATE[inst]!;
  const n = Math.max(2, Math.round(dur * rate));
  const step = dur / n;
  const rest = techs.filter((x) => x === 'rou' || x === 'an');
  for (let i = 0; i < n; i++) {
    const shape = 0.75 + 0.25 * Math.sin((Math.PI * i) / n);
    const v = vel * shape * (i % 2 ? 0.82 : 1) * (0.92 + 0.08 * c.rng());
    const last = i === n - 1;
    c.out.push({
      t: t + i * step,
      dur: step,
      part,
      inst,
      midi,
      vel: v,
      techs: i === 0 ? rest.filter((x) => x === 'an') : [],
      ring: last ? (RING[inst] ?? 1) : step * 1.4,
    });
  }
}

function compileBlock(def: TrackDef, names: readonly string[], seed: number): CompiledBlock {
  const c: Ctx = { def, rng: mulberry32(seed), out: [], phrase: 0 };
  let t = 0;
  const sections: { name: string; t: number }[] = [];
  for (const name of names) {
    sections.push({ name, t });
    t = compileSection(c, name, t);
  }
  const events = c.out
    .filter((e) => e.t < t)
    .sort((a, b) => a.t - b.t || a.part.localeCompare(b.part) || a.midi - b.midi);
  return { events, dur: t, sections };
}

/**
 * Compiles a track. `loop` is how many times the loop has already played: each pass varies a
 * little (its own seed), and the same pass always comes out the same.
 */
export function compileTrack(def: TrackDef, loop = 0): CompiledTrack {
  return {
    def,
    intro: def.intro?.length ? compileBlock(def, def.intro, def.seed * 31 + 7) : null,
    body: compileBody(def, loop),
  };
}

/** One pass of the loop (the `loop`-th). */
export const compileBody = (def: TrackDef, loop: number): CompiledBlock =>
  compileBlock(def, def.form, def.seed * 7919 + loop * 104729 + 1);

/** Length of one pass of the loop in seconds (the same on every pass). */
export const loopSeconds = (def: TrackDef): number => compileTrack(def).body.dur;
